import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Section from '../components/Section';
import Button from '../components/Button';
import ArticleListItem from '../components/ArticleListItem';
import SEO from '../components/SEO';
import content from '../data/content.json';

const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug
    ? content.articles.full_articles[slug as keyof typeof content.articles.full_articles]
    : null;

  if (!article) {
    return (
      <Section title="Artikkel ikke funnet" centered spacing="lg">
        <p className="text-lg text-gray-600 mb-8">
          Artikkelen du leter etter eksisterer ikke.
        </p>
        <Link to="/">
          <Button>Tilbake til forsiden</Button>
        </Link>
      </Section>
    );
  }

  const paragraphs = article.content.flatMap(sec => sec.paragraphs);

  // Map article slugs to their images
  const articleImages: { [key: string]: string } = {
    'kjelleretasje-kan-vaere-uegnet-for-boligformal': '/privateImages/kjeller01.webp',
    'rad-dersom-sameiet-vurderer-salg-av-raloft': '/privateImages/loft.webp',
  };

  const articleImage = slug ? articleImages[slug] : null;

  // Topic-aware CTA: link each article to the most relevant practice-area page.
  const relatedService: { [key: string]: { url: string; heading: string; label: string } } = {
    'kjelleretasje-kan-vaere-uegnet-for-boligformal': {
      url: '/fast-eiendom',
      heading: 'Trenger du bistand i en sak om fast eiendom?',
      label: 'Les om fast eiendom',
    },
    'rad-dersom-sameiet-vurderer-salg-av-raloft': {
      url: '/fast-eiendom',
      heading: 'Trenger du bistand i en sameie- eller eiendomssak?',
      label: 'Les om fast eiendom',
    },
  };
  const svc = slug ? relatedService[slug] : null;

  // Get other articles for "related articles" section
  const otherArticles = content.articles.listed_on_homepage
    .filter(a => a.slug !== slug)
    .slice(0, 2);

  // Generate description from first paragraph
  const description = paragraphs[0]?.substring(0, 160) || article.title;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "datePublished": article.date,
    "author": {
      "@type": "Organization",
      "name": "Advokatfirmaet Bruland"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Advokatfirmaet Bruland",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bruland.no/privateImages/brulandlogoFooter.webp"
      }
    },
    "description": description,
    "image": articleImage ? `https://bruland.no${articleImage}` : "https://bruland.no/privateImages/brulandlogoFooter.webp"
  };

  return (
    <>
      <SEO
        title={article.title}
        description={description}
        keywords="juridisk artikkel, advokat råd, juridisk informasjon, arv og eiendom"
        canonicalUrl={`/article/${slug}`}
        ogType="article"
        ogImage={articleImage ? `https://bruland.no${articleImage}` : undefined}
        structuredData={structuredData}
      />
      {/* Article Header */}
      <Section background="gray" spacing="md" centered container="narrow">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-serif">
          {article.title}
        </h1>
        <p className="text-lg text-gray-600 mb-4">{article.date}</p>
        <div className="w-24 h-1 bg-accent mx-auto"></div>
      </Section>

      {/* Article Content */}
      <Section background="white" spacing="lg" container="narrow">
        {/* Featured Image */}
        {articleImage ? (
          <img
            src={articleImage}
            alt={`Illustrasjonsbilde for artikkelen: ${article.title}`}
            className="w-full h-96 object-cover rounded-xl mb-12"
          />
        ) : (
          <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-xl mb-12 flex items-center justify-center">
            <svg className="w-24 h-24 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}

        {/* Article Text */}
        <div className="article-content">
          {paragraphs.map((para, idx) => (
            <p
              key={idx}
              className={`mb-6 ${
                idx === 0
                  ? 'text-xl text-gray-800 leading-relaxed'
                  : 'text-lg text-gray-700 leading-relaxed'
              }`}
            >
              {para}
            </p>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-primary/5 border-2 border-primary/20 rounded-xl p-8 text-center my-12">
          <h3 className="text-2xl font-bold mb-4 font-serif">
            {svc ? svc.heading : 'Trenger du juridisk rådgivning?'}
          </h3>
          <p className="text-gray-600 mb-6">
            Kontakt oss for en uforpliktende samtale om din sak.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button size="lg">Kontakt oss</Button>
            </Link>
            {svc && (
              <Link to={svc.url}>
                <Button variant="outline" size="lg">{svc.label}</Button>
              </Link>
            )}
          </div>
        </div>
      </Section>

      {/* Related Articles */}
      {otherArticles.length > 0 && (
        <Section title="Relaterte artikler" background="gray" spacing="lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {otherArticles.map((art, idx) => (
              <ArticleListItem
                key={idx}
                date={art.date}
                title={art.title}
                slug={art.slug}
                linkText="Les mer"
                image={art.slug ? articleImages[art.slug] : undefined}
              />
            ))}
          </div>
        </Section>
      )}
    </>
  );
};

export default Article;

